import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { workOrderPickerLabel } from '../utils/workOrderLabel';
import { getDisplayName } from '../utils/formatters';

/**
 * useTimesheets - Replicates loadTimesheets from Timesheets.js
 * Uses backend RPC get_filtered_timesheets for filtering and calculations
 * Falls back to direct table query if RPC fails
 */
export const useTimesheets = (companyId, {
  employeeId,
  workOrderId,
  status,
  dateStart,
  dateEnd,
  overtimeOnly,
  searchTerm,
  employees // Required for mapping employee data
} = {}) => {
  return useQuery({
    queryKey: ['timesheets', companyId, employeeId, workOrderId, status, dateStart, dateEnd, overtimeOnly, searchTerm, (employees || []).map(e => e.id).sort().join(',')],
    queryFn: async () => {
      const supabase = getSupabaseClient();

      let data = null;
      let error = null;

      // Try RPC first
      try {
        const result = await supabase.rpc('get_filtered_timesheets', {
          p_company_id: companyId,
          // A blank filter is "no filter" (as in the fallback below); sent as "" it is a 22P02
          // uuid error and the page silently drops to the fallback query.
          p_employee_id: employeeId || null,
          p_work_order_id: workOrderId && workOrderId !== 'all' ? workOrderId : null,
          p_status: status !== 'all' ? status : 'all',
          p_date_start: dateStart || null,
          p_date_end: dateEnd || null,
          p_overtime_only: overtimeOnly || false,
          p_search_term: searchTerm || null
        });
        
        data = result.data;
        error = result.error;
      } catch (e) {
        console.warn('⚠️ RPC get_filtered_timesheets failed, falling back to direct query:', e.message);
      }

      // Fallback to direct query if RPC fails
      if (error || !data) {
        console.warn('⚠️ Using fallback direct query for timesheets');
        
        let query = supabase
          .from('employee_timesheets')
          .select('*')
          .eq('company_id', companyId);

        if (employeeId) {
          query = query.eq('employee_id', employeeId);
        }
        if (workOrderId && workOrderId !== 'all') {
          query = query.eq('work_order_id', workOrderId);
        }
        if (status && status !== 'all') {
          query = query.eq('status', status);
        }
        if (dateStart) {
          query = query.gte('date', dateStart);
        }
        if (dateEnd) {
          query = query.lte('date', dateEnd);
        }
        if (overtimeOnly) {
          query = query.gt('overtime_hours', 0);
        }
        // #3350: the fallback ignored searchTerm, so an RPC failure silently returned every row
        // while the search box still showed a term. Same fields as get_filtered_timesheets:
        // notes, job title / number, employee name. PostgREST cannot OR across joins, so jobs are
        // resolved to ids and employees are matched on the list this hook already holds.
        const term = String(searchTerm || '').trim();
        if (term) {
          const safe = term.replace(/[%,()*]/g, ' ');
          const { data: woMatches } = await supabase
            .from('work_orders').select('id').eq('company_id', companyId)
            .or(`title.ilike.*${safe}*,work_order_number.ilike.*${safe}*`).limit(500);
          const lower = term.toLowerCase();
          const empIds = (employees || []).filter((e) => [
            e.name, e.full_name, e.first_name, e.last_name, `${e.first_name || ''} ${e.last_name || ''}`,
          ].some((v) => v && String(v).toLowerCase().includes(lower))).map((e) => e.id);
          const parts = [`notes.ilike.*${safe}*`];
          if ((woMatches || []).length) parts.push(`work_order_id.in.(${woMatches.map((w) => w.id).join(',')})`);
          if (empIds.length) parts.push(`employee_id.in.(${empIds.join(',')})`);
          query = query.or(parts.join(','));
        }

        const result = await query.order('date', { ascending: false });
        data = result.data || [];
        error = result.error;
      }

      if (error) {
        console.error('Failed to load timesheets:', error);
        throw error;
      }

      // Create employee lookup map for client-side matching
      const employeeMap = {};
      (employees || []).forEach(emp => {
        employeeMap[emp.id] = emp;
      });

      // Map backend data to frontend format
      const timesheetsWithStatus = (data || []).map(timesheet => {
        const employee = employeeMap[timesheet.employee_id];
        return {
          ...timesheet,
          full_name: getDisplayName(employee, 'Unknown Employee'),
          employee_name: getDisplayName(employee, 'Unknown Employee'),
          employees: employee || null,
          job_title: timesheet.job_title || '',
          break_duration: timesheet.break_duration || 0,
        };
      });

      return timesheetsWithStatus;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};

/**
 * useTimesheetEmployees - Replicates loadEmployees from Timesheets.js
 * Fetches employees with user data for the company
 */
export const useTimesheetEmployees = (companyId, currentUserId) => {
  return useQuery({
    queryKey: ['timesheet-employees', companyId],
    queryFn: async () => {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          employee_number,
          first_name,
          last_name,
          user_id,
          employee_compensation (pto_balance_hours)
        `)
        .eq('company_id', companyId)
        .order('last_name');

      if (error) {
        console.error('Error loading employees:', error);
        throw error;
      }

      // Transform data to match expected format
      const mappedEmployees = (data || []).map(emp => ({
        id: emp.id,
        employee_number: emp.employee_number,
        full_name: getDisplayName(emp),
        first_name: emp.first_name || '',
        last_name: emp.last_name || '',
        user_id: emp.user_id,
        pto_balance_hours: emp.employee_compensation?.pto_balance_hours ?? null
      }));

      // Find the employee record that matches the current authenticated user
      const currentEmp = mappedEmployees.find(emp => emp.user_id === currentUserId);

      return {
        employees: mappedEmployees,
        currentEmployee: currentEmp || null
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};

/**
 * useTimesheetJobs - Replicates loadJobs from Timesheets.js
 * Fetches active work orders assigned to the user
 * If user is admin/owner, returns all company work orders
 */
export const useTimesheetJobs = (companyId, userId, isAdminUser = false) => {
  return useQuery({
    queryKey: ['timesheet-jobs', companyId, userId, isAdminUser],
    queryFn: async () => {
      const supabase = getSupabaseClient();

      // Takes a resolved BOOLEAN, not a role string. It used to take `userRole`
      // and compare it to 'OWNER'/'ADMIN' in UPPERCASE, but the live
      // user_role_enum is lowercase ('owner' = 128 of 169 users), so that test
      // could never be true. Callers now pass isAdmin(user) from roleUtils,
      // which is the one place that knows how to answer this.

      // Build query - admin/owner sees all, others see only their assigned work orders
      let query = supabase
        .from('work_orders')
        .select('id, work_order_number, quote_number, title, status, customer_id, scheduled_start, created_at')
        .eq('company_id', companyId)
        .in('status', ['scheduled', 'in_progress'])
        .order('status', { ascending: false })
        .order('created_at', { ascending: false });

      // work_orders.assigned_to holds a users.id, NOT an employees.id
      // (CLAUDE.md §15 - they are disjoint id spaces). Verified live 2026-08-16:
      // 121 of 121 assigned work orders match users.id, only 13 match
      // employees.id. The previous code looked up employees.id and compared it
      // to this column, so it matched almost nothing and the Job dropdown came
      // back empty even where jobs existed.
      if (!isAdminUser && userId) {
        query = query.eq('assigned_to', userId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading work orders:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      // Map work_orders data to jobs format with number and status
      const mappedJobs = data.map(wo => ({
        id: wo.id,
        job_title: wo.title,
        work_order_number: wo.work_order_number,
        status: wo.status,
        customer_id: wo.customer_id,
        // Same label as the Expenses, Purchase Orders and Documents job pickers.
        // The previous expression fell back to the raw uuid when work_order_number
        // was null -- unambiguous, but "#3f2a1b9c-... - Weekly Maintenance" is not
        // something a contractor can read, and null is exactly the case that matters:
        // recurring-spawned visits never get a work_order_number, so a whole series
        // rendered as identical rows.
        displayLabel: workOrderPickerLabel(wo)
      }));

      return mappedJobs;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!userId,
  });
};

/**
 * useTimesheetPTORequests - Replicates loadPTORequests from Timesheets.js
 * Fetches PTO requests for the current employee
 */
export const useTimesheetPTORequests = (companyId, employeeId) => {
  return useQuery({
    queryKey: ['timesheet-pto', companyId, employeeId],
    queryFn: async () => {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase
        .from('employee_time_off')
        .select('*')
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading PTO requests:', error);
        throw error;
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!employeeId,
  });
};
